import { useState, useRef, useEffect } from "react";
import { Icon } from "./Icons";
import { DarkToggle } from "./DarkToggle";
import { api } from "../../api";
import gsap from "gsap";
import { ClinicIllustration, ToolsIllustration, DeliveryIllustration } from "./AuthIllustrations";
import { auth, googleProvider } from "../../firebase";
import { signInWithPopup } from "firebase/auth";

const SLIDES = [
  { Illustration: ClinicIllustration, title: "Connect to the K-Dental Network", desc: "India's premier marketplace for high-performance dental supplies." },
  { Illustration: ToolsIllustration, title: "Premium Clinical Materials", desc: "Access top-tier equipment curated for professional clinics." },
  { Illustration: DeliveryIllustration, title: "Seamless Supply Logistics", desc: "Keep your inventory fully stocked with our fast nationwide delivery." }
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const AnimatedLockIcon = () => {
  const ringsRef = useRef(null);
  const lockRef = useRef(null);

  useEffect(() => {
    gsap.to(ringsRef.current, { scale: 1.15, opacity: 0.5, duration: 2, repeat: -1, yoyo: true, ease: "sine.inOut" });
    gsap.to(lockRef.current, { y: -6, duration: 1.8, repeat: -1, yoyo: true, ease: "power1.inOut" });
  }, []);

  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
      <svg width="80" height="80" viewBox="0 0 100 100" fill="none">
        <g ref={ringsRef} transform="translate(50,50)">
          <circle cx="0" cy="0" r="32" fill="var(--blue)" opacity="0.1" />
          <circle cx="0" cy="0" r="42" fill="none" stroke="var(--blue)" strokeWidth="1" opacity="0.3" strokeDasharray="4 4" />
        </g>
        <g ref={lockRef} transform="translate(35,30)">
          <path d="M 5 15 L 5 10 C 5 -5, 25 -5, 25 10 L 25 15" stroke="var(--blue)" strokeWidth="4" strokeLinecap="round" fill="none" />
          <rect x="0" y="15" width="30" height="24" rx="6" fill="#ffffff" stroke="var(--blue)" strokeWidth="3" />
          <circle cx="15" cy="24" r="3" fill="var(--blue)" />
          <path d="M 14 26 L 16 26 L 17 32 L 13 32 Z" fill="var(--blue)" />
        </g>
        <path d="M 20 20 L 23 25 L 28 28 L 23 31 L 20 36 L 17 31 L 12 28 L 17 25 Z" fill="var(--cyan)" opacity="0.6" />
      </svg>
    </div>
  );
};

export function LoginPage({ type, onBack, onLogin, onGoSignup, dark, setDark }) {
  const [email, setEmail]       = useState("");
  const [pass, setPass]         = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [err, setErr]           = useState("");
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);
  
  const [isFocused, setIsFocused] = useState(false);
  const [slide, setSlide] = useState(0);
  
  const isAdmin = type === "admin";

  const cardRef = useRef(null);
  const leftRef = useRef(null);
  const formRef = useRef(null);
  const imgRef = useRef(null);
  const slideContentRef = useRef(null);

  // 1. Initial Spring Entrance Animations
  useEffect(() => {
    // Card drops in with a spring layout-shift
    gsap.fromTo(cardRef.current, 
      { y: 40, opacity: 0, scale: 0.95 },
      { y: 0, opacity: 1, scale: 1, duration: 1.2, ease: "elastic.out(1, 0.75)" }
    );

    // Left pane illustration soft entry
    gsap.fromTo(leftRef.current,
      { x: -30, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.1 }
    );

    // Right pane form staggered reveal
    const formEls = formRef.current?.children;
    if (formEls) {
      gsap.fromTo(formEls,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.08, ease: "back.out(1.2)", delay: 0.2 }
      );
    }
  }, []);

  // 2. Interactive Feed (Image rotates on input focus)
  useEffect(() => {
    if (imgRef.current) {
      if (isFocused) {
        gsap.to(imgRef.current, { rotation: 2, scale: 1.04, duration: 0.7, ease: "elastic.out(1, 0.6)" });
      } else {
        gsap.to(imgRef.current, { rotation: 0, scale: 1, duration: 0.5, ease: "power3.out" });
      }
    }
  }, [isFocused]);

  // 3. Carousel Autoplay logic
  useEffect(() => {
    const timer = setInterval(() => {
      // Fade out
      gsap.to(slideContentRef.current, { opacity: 0, y: -10, duration: 0.3, onComplete: () => {
        setSlide(s => (s + 1) % SLIDES.length);
        // Fade in
        gsap.fromTo(slideContentRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" });
      }});
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSocialLogin = async (provider) => {
    setErr(""); setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      const res = await api("/api/auth/firebase", {
        method: "POST", auth: false,
        body: { token, rememberMe }
      });
      if (isAdmin && res.user.role !== "admin") { setErr("This portal is for administrators only."); return; }
      if (!isAdmin && res.user.role === "admin") { setErr("Please use the Admin Portal to sign in."); return; }
      onLogin(res.user, res.token, rememberMe);
    } catch (error) {
      console.error(error);
      if (error.code !== "auth/popup-closed-by-user" && error.code !== "auth/cancelled-popup-request") {
        const errorMsg = error.message || "Social login failed. Please try again.";
        setErr(errorMsg);
        alert("🚨 Google Login Error:\n\n" + errorMsg + "\n\n(Please tell me this exact error!)");
      }
    } finally {
      setLoading(false);
    }
  };

  const handle = async () => {
    if (!email.trim() || !pass) { setErr("Please enter your email and password."); return; }
    setLoading(true); setErr("");
    try {
      const { user, token } = await api("/api/auth/login", { 
        method:"POST", auth:false, 
        body:{ email: email.trim().toLowerCase(), password: pass, rememberMe }
      });
      if (isAdmin && user.role !== "admin") { setErr("This portal is for administrators only."); return; }
      if (!isAdmin && user.role === "admin") { setErr("Please use the Admin Portal to sign in."); return; }
      onLogin(user, token, rememberMe);
    } catch (err) {
      setErr(err.message || "Invalid email or password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="clean-auth-page">
      <div className="clean-auth-card" ref={cardRef}>
        
        {/* Left Carousel Pane */}
        <div className="clean-left" ref={leftRef}>
          <div className="clean-carousel">
            <div ref={slideContentRef} style={{ display:"flex", flexDirection:"column", alignItems:"center", height:"100%", justifyContent:"center" }}>
              <div ref={imgRef} className="clean-carousel-img">
                {(() => {
                  const CurrentIllustration = SLIDES[slide].Illustration;
                  return <CurrentIllustration />;
                })()}
              </div>
              <h3>{SLIDES[slide].title}</h3>
              <p>{SLIDES[slide].desc}</p>
            </div>

            <div className="clean-dots">
              {SLIDES.map((_, i) => (
                <div key={i} className={`clean-dot ${i === slide ? "active" : ""}`} />
              ))}
            </div>
          </div>
        </div>

        {/* Right Form Pane */}
        <div className="clean-right">
          <div className="clean-topbar">
            <div className="clean-brand">
              <img src="/logo.png" alt="Logo" />
              K Dental
            </div>
            <div style={{ display:"flex", alignItems:"center", gap: 24 }}>
              <DarkToggle dark={dark} setDark={setDark}/>
              {!isAdmin && <button className="clean-top-link" onClick={onGoSignup}>Register now</button>}
            </div>
          </div>

          <div className="clean-right-content" ref={formRef}>
            <AnimatedLockIcon />
            <h2 style={{ textAlign: "center" }}>{isAdmin ? "Admin Login" : "Log in now"}</h2>

            {!isAdmin && (
              <>
                <div className="social-btns" style={{ marginBottom: 30, display: "flex", justifyContent: "center" }}>
                  <button className="social-btn" onClick={() => handleSocialLogin(googleProvider)} disabled={loading} style={{ width: "100%", maxWidth: 280 }}><GoogleIcon /> Google</button>
                </div>
                <div className="clean-divider">Or login with email</div>
              </>
            )}

            {err && <div className="clean-err-msg" style={{ marginBottom:14 }}><Icon name="warning" size={13}/> {err}</div>}

            <div className="clean-field">
              <input 
                type="email" 
                placeholder="Email/Phone no." 
                value={email}
                onChange={e => { setEmail(e.target.value); setErr(""); }}
                onKeyDown={e => e.key === "Enter" && handle()}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className={err ? "error" : ""}
              />
            </div>

            <div className="clean-field">
              <div style={{ position: "relative" }}>
                <input 
                  type={showPass ? "text" : "password"} 
                  placeholder="Password" 
                  value={pass}
                  onChange={e => { setPass(e.target.value); setErr(""); }}
                  onKeyDown={e => e.key === "Enter" && handle()}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className={err ? "error" : ""}
                  style={{ paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowPass(v=>!v)} style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', transition: 'color 0.2s' }}>
                  <Icon name={showPass ? "eyeOff" : "eye"} size={16}/>
                </button>
              </div>
            </div>

            {/* Remember Me Toggle */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, marginBottom: 8, paddingLeft: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--text3)", userSelect: "none" }}>
                <input 
                  type="checkbox" 
                  checked={rememberMe} 
                  onChange={(e) => setRememberMe(e.target.checked)} 
                  style={{ cursor: "pointer", width: 14, height: 14, accentColor: "var(--blue)" }}
                />
                Remember me
              </label>
              <span style={{ fontSize: 13, color: "var(--blue)", cursor: "pointer" }}>Forgot password?</span>
            </div>

            <button className="clean-btn" onClick={handle} disabled={loading} style={{ background: "var(--blue)", marginTop: 20 }}>
              {loading ? "Processing…" : "Process now"}
            </button>
            
            <p style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "var(--text3)", cursor: "pointer", transition: "color 0.2s" }} onClick={onBack}>
              &larr; Return to Home
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
